import React, { useEffect, useState } from "react";
import { useHistory, useParams } from "react-router-dom";
import { Button } from "@material-ui/core";
import Select from "react-select";
import useFetch from "../../utils/useFetch";
import { openDVImagesPage } from "../../utils/helpers";
import { DVType, ImagesDV } from "./_types";
import InfoOutlinedIcon from "@material-ui/icons/InfoOutlined";
import ReactTooltip from "react-tooltip";
import { AddCircleOutline, Cached, CheckCircleOutline } from "@material-ui/icons";
import { getFetch } from "../../utils/fetchUtils";

type OutletDVStatsResponseSub = {
  checked: number;
  validated: number;
  pending: number;
  label_pending: ImagesDV["label_pending"];
};

type Period = {
  label: string;
  value: string;
  complete: boolean;
  last_updated: string;
  overview: OutletDVStatsResponseSub;
  categories: OutletDVStatsResponseSub;
  tracked_items: OutletDVStatsResponseSub;
  outliers: {
    approved: number;
    pending: number;
    devices_weight_threshold: Record<string, number>;
  };
};

type OutletDVStatsResponse = {
  outlet_name: string;
  property_name: string;
  dv_date: string | null;
  oldest_unlabelled: string | null;
  periods: Period[];
};

const keys: Record<DVType, string> = {
  overview: "Top Wasted Items",
  categories: "Categories",
  tracked_items: "Tracked Items",
  outlier: "Outliers",
};

const OutletDataValidity = () => {
  const history = useHistory();
  const params = useParams<{ outlet_id: string }>();
  const outlet_id = Number(params.outlet_id);

  useEffect(() => {
    if (!outlet_id) history.push("/dataValidity");
  }, [outlet_id, history]);

  const [reloadCounter, setReloadCounter] = useState(0);
  const fetchedData = useFetch<OutletDVStatsResponse>(
    `/get-outlet-dv-stats?outlet_id=${outlet_id}&reload=${reloadCounter}`,
    { do_load: Boolean(outlet_id) }
  );

  // flag to trigger agg refresh; when the call is successful, begin
  // waiting for the new refreshed aggs
  const [refreshAggs, setRefreshAggs] = useState(false);
  const [waitAggs, setWaitAggs] = useState(false);
  useEffect(() => {
    if (refreshAggs) {
      const controller = new AbortController();

      getFetch(`/update-dv-date?outlet_id=${outlet_id}`, {
        api: global.api_url,
        data: null,
        signal: controller.signal,
      })
        .then(() => {
          setRefreshAggs(false);
          setWaitAggs(true);
        })
        .catch((e) => {
          alert(`Something went wrong... Couldn't recompute aggs and DV date: ${e}`);
          setRefreshAggs(false);
        });

      return () => controller.abort();
    }
  }, [refreshAggs, outlet_id]);

  // if we're waiting for aggs to refresh, keep refreshing stats every two seconds
  // as long as we're waiting
  useEffect(() => {
    if (waitAggs) {
      const refetch = () => setReloadCounter((c) => c + 1);

      refetch();
      const interval = setInterval(refetch, 2000);
      return () => clearInterval(interval);
    }
  }, [waitAggs]);

  // on first stats fetch, pre-select first incomplete month, or if all complete then last month;
  // on all next stats fetch (while waiting for new aggs), check if the newly refreshed aggs
  // were retrieved (new last_updated, or no months at all retrieved), and if so stop waiting
  const [selectedPeriod, setSelectedPeriod] = useState<Period | null>(null);
  useEffect(() => {
    if (fetchedData.loaded && (selectedPeriod === null || waitAggs)) {
      let newSelectedPeriod = null;

      const periods = fetchedData.data.periods;
      if (periods.length) {
        if (waitAggs) {
          // try to find the same month as we had previously selected
          const sameMonth = periods.find((p) => p.value === selectedPeriod.value);
          if (sameMonth) newSelectedPeriod = sameMonth;
        }

        if (!newSelectedPeriod) {
          const oldestIncomplete = periods.find((p) => !p.complete);
          newSelectedPeriod = oldestIncomplete || periods[periods.length - 1];
        }
      }

      if (waitAggs && (!newSelectedPeriod || newSelectedPeriod.last_updated !== selectedPeriod?.last_updated))
        setWaitAggs(false);

      setSelectedPeriod(newSelectedPeriod);
    }
  }, [fetchedData, selectedPeriod, waitAggs]);

  // show when the last agg refresh was in minutes and seconds
  const [lastRecomputeDiff, setLastRecomputeDiff] = useState(null);
  useEffect(() => {
    if (selectedPeriod) {
      const refreshTime = () => {
        const diffMs = Date.now() - new Date(selectedPeriod.last_updated).getTime();

        const mins = Math.floor(diffMs / 60000);
        const secs = Math.floor((diffMs % 60000) / 1000);

        setLastRecomputeDiff(`${mins}m ${secs}s ago`);
      };

      refreshTime();
      const interval = setInterval(refreshTime, 1000);
      return () => clearInterval(interval);
    }

    setLastRecomputeDiff("N/A");
  }, [selectedPeriod]);

  // memorize some values so they don't disappear in between fetches...
  const [outletInfo, setOutletInfo] = useState(null);
  useEffect(() => {
    if (fetchedData.loaded) {
      setOutletInfo({
        outlet_name: fetchedData.data.outlet_name,
        property_name: fetchedData.data.property_name,
      });
    }
  }, [fetchedData]);

  // we need to remove the "overflow: hidden" from <body> and put it back on unmount
  useEffect(() => {
    document.getElementsByTagName("body")[0].style.overflowY = "auto";
    return () => {
      document.getElementsByTagName("body")[0].style.overflowY = "hidden";
    };
  });

  useEffect(() => {
    if (outletInfo) window.document.title = `DV: ${outletInfo.property_name} > ${outletInfo.outlet_name}`;

    return () => {
      window.document.title = "KITRO Labelling Dashboard";
    };
  }, [outletInfo]);

  if (fetchedData.error)
    return (
      <div>
        Error loading DV stats for outlet #{outlet_id}: {fetchedData.error}
      </div>
    );

  if (!outletInfo) return <div>Loading DV stats for outlet #{outlet_id}...</div>;

  const refreshingAggs = waitAggs || refreshAggs;

  return (
    <div className="animated fadeIn">
      <div>
        <h1 style={{ marginBottom: "20px" }}>
          {outletInfo.property_name} &gt; {outletInfo.outlet_name}
        </h1>

        <div className="filterContainer">
          <div className="col-auto">
            <div className="input-group">
              <div className="input-group-prepend">
                <span className="input-group-text">Last Recompute</span>
              </div>
              <span className="nonChangeableValue">{lastRecomputeDiff}</span>
            </div>
          </div>

          <div style={{ display: "flex" }}>
            <Button
              variant="outlined"
              startIcon={refreshingAggs ? <Cached style={{ animation: "spin 1s linear infinite" }} /> : <Cached />}
              onClick={() => setRefreshAggs(true)}
              disabled={refreshingAggs}
            >
              {refreshingAggs ? "Recomputing..." : "Recompute Now"}
            </Button>
          </div>
        </div>

        <div className="filterContainer">
          <div className="col-auto">
            <div className="input-group">
              <div className="input-group-prepend">
                <span className="input-group-text" id="period">
                  Period
                </span>
              </div>

              {!refreshingAggs && fetchedData.data?.periods.length ? (
                <Select
                  disabled={waitAggs}
                  className="periodSelect"
                  aria-describedby="period"
                  name="selectPeriod"
                  key="selectPeriod"
                  options={fetchedData.data.periods}
                  value={selectedPeriod}
                  onChange={setSelectedPeriod}
                />
              ) : (
                <span className="nonChangeableValue">
                  {refreshingAggs ? "Recomputing..." : "No months available for DV"}
                </span>
              )}
            </div>
          </div>

          <div className="col-auto">
            <div className="input-group">
              <div className="input-group-prepend">
                <span className="input-group-text">Data Validity</span>
              </div>
              <span className="nonChangeableValue">
                {refreshingAggs ? "Recomputing..." : fetchedData.data?.dv_date || "N/A"}
              </span>
            </div>
          </div>

          <div className="col-auto">
            <div className="input-group">
              <div className="input-group-prepend">
                <span className="input-group-text">Oldest Unlabelled</span>
              </div>
              <span className="nonChangeableValue">
                {refreshingAggs ? "Recomputing..." : fetchedData.data?.oldest_unlabelled || "N/A"}
              </span>
            </div>
          </div>
        </div>

        <hr />

        {!refreshingAggs && selectedPeriod && (
          <div className="cardContainer">
            {Object.entries(keys).map(([key, title]) => (
              <StatCard
                key={key}
                type={key}
                title={title}
                outlet_id={outlet_id}
                dv_images_payload={{
                  outlet_id,
                  outlet_name: outletInfo.outlet_name,
                  property_name: outletInfo.property_name,
                  period: selectedPeriod.value,
                }}
                {...selectedPeriod[key === "outlier" ? "outliers" : key]}
              />
            ))}
          </div>
        )}
      </div>
    </div>
  );
};

const PendingDVTooltip = (props) => (
  <ReactTooltip
    class="pendingDVTooltip"
    id={props.type}
    event="click"
    globalEventOff="click"
    place="right"
    type="info"
    arrowColor="transparent"
    clickable
  >
    {Object.keys(props.pendingLabel)
      .filter((l) => props.pendingLabel[l].pending > 0)
      .map((label) => {
        const labelSplit = label.split(">");
        const labelWithoutDescription =
          labelSplit.length > 1 ? labelSplit.slice(0, -1).reduce((prev, label) => prev + ">" + label) + ">" : "";

        return (
          <div className="text-big" key={label}>
            {labelWithoutDescription}{" "}
            <b>
              {labelSplit[labelSplit.length - 1]} : {props.pendingLabel[label].pending}{" "}
            </b>
          </div>
        );
      })}
  </ReactTooltip>
);

type StatCardParams = {
  title: string;
  type: ImagesDV["dv_type"];
  outlet_id: number;
  checked?: number;
  approved?: number;
  validated?: number;
  pending?: number;
  devices_weight_threshold?: Record<string, number>;
  dv_images_payload: { outlet_id: number; period: string; outlet_name: string; property_name: string };
  label_pending?: ImagesDV["label_pending"];
};

const StatCard = (props: StatCardParams) => {
  const history = useHistory();

  const checkMoreImages = () =>
    openDVImagesPage(history, {
      ...props.dv_images_payload,
      mode: "check images",
      dv_type: props.type,
      label_pending: { ...props.label_pending },
    });

  const reviewCheckedImages = () =>
    openDVImagesPage(history, {
      ...props.dv_images_payload,
      mode: "review images",
      dv_type: props.type,
      label_pending: { ...props.label_pending },
    });

  const labelPending = (labels: ImagesDV["label_pending"]) =>
    Object.keys(labels).filter((label) => labels[label].pending > 0);

  return (
    <div className="statCard">
      <div className={props.type === "outlier" ? "statCardTitle statCardOutliersTitle" : "statCardTitle"}>
        <h2>{props.title}</h2>
      </div>
      {props.type === "outlier" ? (
        <div className="statCardInfo">
          <div key="accepted">
            # Approved: <b>{props.approved}</b>
          </div>
          <div key="pending">
            # Pending: <b>{props.pending}</b>
          </div>
          <div key="thresholds">
            <table>
              <thead>
                <tr>
                  <th>Device</th>
                  <th>Outliers weight threshold (g)</th>
                </tr>
              </thead>
              <tbody>
                {props.devices_weight_threshold ? (
                  Object.keys(props.devices_weight_threshold).map((device) => (
                    <tr key={device}>
                      <td key="device">{device}</td>
                      <td key="threshold">{props.devices_weight_threshold[device]}</td>
                    </tr>
                  ))
                ) : (
                  <tr>
                    <td>NA</td>
                  </tr>
                )}
              </tbody>
            </table>
          </div>

          <div className="buttonGroup">
            <Button startIcon={<AddCircleOutline />} key="check" variant="outlined" onClick={checkMoreImages}>
              Check outliers
            </Button>
            <Button startIcon={<CheckCircleOutline />} key="review" variant="outlined" onClick={reviewCheckedImages}>
              Review checked outliers
            </Button>
          </div>
        </div>
      ) : (
        <div className="statCardInfo">
          <div key="checked">
            # Checked: <b>{props.checked}</b>
          </div>
          <div key="validated">
            # Validated: <b>{props.validated}</b>
          </div>
          <div className="statCardPending" key="pending">
            # Pending
            {Object.keys(labelPending(props.label_pending)).length > 0 ? (
              <sup>
                <InfoOutlinedIcon data-tip data-for={props.type} style={{ fontSize: 16, marginLeft: "5px" }} />
                <PendingDVTooltip type={props.type} pendingLabel={props.label_pending} />
              </sup>
            ) : (
              <div></div>
            )}
            : &nbsp;
            <b>{props.pending}</b>
          </div>

          <div className="buttonGroup">
            <Button startIcon={<AddCircleOutline />} key="check_more" variant="outlined" onClick={checkMoreImages}>
              Check more images
            </Button>
            <br />
            <Button startIcon={<CheckCircleOutline />} key="review" variant="outlined" onClick={reviewCheckedImages}>
              Review checked images
            </Button>
          </div>
        </div>
      )}
    </div>
  );
};

export default OutletDataValidity;
